import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Upload, Check, IndianRupee, X } from "lucide-react";
import vendor_profile_icon from "../../../assets/profile_icon.svg";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "utils/constant";
import { MdExpandMore } from "react-icons/md";
import { useAlert } from "components/UI_components/Alert";
import { resolveTransactionFeedback } from "utils/transactionFeedback";

function MaterialSelectionModal({
  isOpen,
  onClose,
  materialsLibrary,
  selectedMaterials = [],
  activeMaterialId,
  onSelectMaterial,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen) setSearchQuery("");
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredMaterials = materialsLibrary.filter((material) => {
    const q = searchQuery.toLowerCase();
    const name = (material?.material_name || "").toLowerCase();
    const sub = (material?.subcategory || "").toLowerCase();
    return name.includes(q) || sub.includes(q);
  });

  return (
    <div className="w-full border border-gray-300 rounded-xl bg-white p-3 mt-2 mb-1">
      <div className="flex items-center justify-between border-b border-gray-200 pb-2">
        <div className="text-base font-medium text-[#232323]">Select Material</div>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
        >
          <X size={18} />
        </button>
      </div>

      <div className="pt-2">
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search Your Material"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full text-sm bg-[#F3F4F6] rounded-lg border-0 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </div>
        </div>
      </div>

      <div className="max-h-56 overflow-y-auto pt-2">
        <div className="space-y-1">
          {filteredMaterials.map((material) => {
            const alreadySelectedInOtherRow = selectedMaterials.some(
              (row) =>
                row.id !== activeMaterialId && row.materialId === material.id,
            );
            return (
              <button
                key={material.id}
                onClick={() => {
                  if (!alreadySelectedInOtherRow) onSelectMaterial(material);
                }}
                className={`w-full flex items-center gap-3 px-2 py-2 rounded-md border border-transparent text-left transition ${
                  alreadySelectedInOtherRow
                    ? "cursor-not-allowed opacity-70"
                    : "hover:bg-gray-50"
                }`}
                disabled={alreadySelectedInOtherRow}
                title={alreadySelectedInOtherRow ? "Already added" : "Click to select"}
              >
                <div className="flex-1">
                  <div className="font-medium text-[#232323]">
                    {material.material_name}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  {material?.specification && (
                    <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                      {material.specification}
                    </span>
                  )}
                  {material?.subcategory && (
                    <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                      {material.subcategory}
                    </span>
                  )}
                  {material?.uom && (
                    <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                      {material.uom}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
          {filteredMaterials.length === 0 && (
            <div className="text-sm text-gray-500 p-4">No materials found.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const VendorPurchase = ({ vendor, onClose }) => {
  const [withPO, setWithPO] = useState(false);
  const [library, setLibrary] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [deliveryDateError, setDeliveryDateError] = useState(null);
  const [selectedMaterialError, setSelectedMaterialError] = useState(null);
  const [quantityError, setQuantityError] = useState(null);
  const [rateError, setRateError] = useState(null);
  const [paidAmountError, setPaidAmountError] = useState(null);
  const [projectError, setProjectError] = useState(null);
  const [showSitesDropdown, setShowSitesDropdown] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState("Upcoming");
  const [deliveryDate, setDeliveryDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [paidAmount, setPaidAmount] = useState("");
  const [materials, setMaterials] = useState([
    {
      id: 1,
      name: "Select Material",
      quantity: "",
      unit: "Unit",
      rate: "",
      total: "",
    },
  ]);
  const [customExpenditure, setCustomExpenditure] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [purchaseType, setPurchaseType] = useState("null");
  const [purchaseTypeError, setPurchaseTypeError] = useState(null);
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [showMaterialSelector, setShowMaterialSelector] = useState(false);
  const [activeMaterialId, setActiveMaterialId] = useState(null);
  const organisationId = JSON.parse(localStorage.getItem("organization")).id;
  const { addAlert } = useAlert();

  const getMaterials = async () => {
    try {
      const res = await fetch(`${BASE_URL}/org/material-library/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: organisationId }),
      });
      if (!res.ok) throw new Error("Failed to fetch materials");
      const data = await res.json();
      setLibrary(data || []);
    } catch (err) {
      console.error("Error fetching materials:", err);
    }
  };

  const getSites = async () => {
    const res = await fetch(
      `${BASE_URL}/sites/organizations/${organisationId}/sites/`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
      },
    );
    const data = await res.json();
    setSites(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    getMaterials();
    getSites();
  }, []);

  // ─── Clear all fields ────────────────────────────────────────────────────────
  const handleClear = () => {
    setPurchaseType("null");
    setSelectedSite(null);
    setDeliveryDate(new Date().toISOString().split("T")[0]);
    setDeliveryStatus("Upcoming");
    setMaterials([
      {
        id: 1,
        name: "Select Material",
        quantity: "",
        unit: "Unit",
        rate: "",
        total: "",
      },
    ]);
    setPaymentMethod(null);
    setPaymentStatus(null);
    setPaidAmount("");
    setRemarks("");
    setReceiptFiles([]);
    setCustomExpenditure([]);
    setShowMaterialSelector(false);
    setActiveMaterialId(null);
    // Reset errors
    setDeliveryDateError(null);
    setSelectedMaterialError(null);
    setQuantityError(null);
    setRateError(null);
    setPaidAmountError(null);
    setProjectError(null);
  };

  const addMaterial = () => {
    const newMaterial = {
      id: Date.now(),
      materialId: null,
      name: "",
      quantity: "",
      unit: "Unit",
      rate: "",
      gst: null,
      gstAmount: "",
      total: "",
    };
    setMaterials([...materials, newMaterial]);
  };

  const handleOpenMaterialSelector = (materialRowId) => {
    setActiveMaterialId(materialRowId);
    setShowMaterialSelector(true);
  };

  const handleSelectMaterialFromModal = (material) => {
    if (!activeMaterialId) return;
    updateMaterial(activeMaterialId, "materialId", material.id.toString());
    setShowMaterialSelector(false);
    setActiveMaterialId(null);
  };

  const removeMaterial = (id) => {
    setMaterials(materials.filter((material) => material.id !== id));
  };

  const updateMaterial = (id, field, value) => {
    setMaterials(
      materials.map((material) => {
        if (material.id === id) {
          const updated = { ...material, [field]: value };

          if (field === "materialId") {
            const selectedMaterial = library.find(
              (m) => m.id === parseInt(value),
            );
            if (selectedMaterial) {
              updated.materialId = parseInt(value);
              updated.name = selectedMaterial.material_name;
              updated.unit = selectedMaterial.uom;
            }
          }

          if (field === "quantity" || field === "rate" || field === "gst") {
            const qty =
              parseFloat(field === "quantity" ? value : updated.quantity) || 0;
            const rate =
              parseFloat(field === "rate" ? value : updated.rate) || 0;
            const baseAmount = qty * rate;
            const gstPercent = field === "gst" ? value : updated.gst;
            const gstAmount = gstPercent ? (baseAmount * gstPercent) / 100 : 0;
            updated.gstAmount = gstAmount;
            updated.total = baseAmount + gstAmount;
          }

          return updated;
        }
        return material;
      }),
    );
  };

  const addCustomExpenditure = () => {
    setCustomExpenditure([
      ...customExpenditure,
      { id: Date.now(), name: "", amount: 0 },
    ]);
  };

  const removeCustomExpenditure = (id) => {
    setCustomExpenditure(customExpenditure.filter((item) => item.id !== id));
  };

  const updateCustomExpenditure = (id, field, value) => {
    setCustomExpenditure(
      customExpenditure.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const calculateTotal = () => {
    const materialsTotal = materials.reduce(
      (sum, material) => sum + (parseFloat(material?.total) || 0),
      0,
    );
    const customTotal = customExpenditure.reduce(
      (sum, item) => sum + (parseFloat(item?.amount) || 0),
      0,
    );
    return materialsTotal + customTotal;
  };

  const handleReceiptUpload = (e) => {
    const files = Array.from(e.target.files);
    setReceiptFiles([...receiptFiles, ...files]);
  };

  const removeReceipt = (index) => {
    setReceiptFiles(receiptFiles.filter((_, i) => i !== index));
  };

  const calculateRemainingPayment = () => {
    const total = calculateTotal();
    const paid = parseFloat(paidAmount) || 0;
    return Math.max(0, total - paid);
  };

  const gstSlabs = [5, 12, 18, 28];

  // ─── Reactive form validity: button brightens only when ALL required fields filled ───
  const isFormValid = useMemo(() => {
    // 1. Purchase type must be selected
    if (purchaseType === "null") return false;
    // 2. If Project, a site must be chosen
    if (purchaseType === "Project" && !selectedSite) return false;
    // 3. At least one material
    if (materials.length === 0) return false;
    // 4. Every material must have id, qty > 0, rate > 0
    const allMaterialsValid = materials.every(
      (m) =>
        m.materialId &&
        m.name &&
        m.name !== "Select Material" &&
        parseFloat(m.quantity) > 0 &&
        parseFloat(m.rate) > 0,
    );
    if (!allMaterialsValid) return false;
    // 5. Payment method selected
    if (!paymentMethod && paymentStatus !== "Full Due") return false;
    // 6. Payment status selected
    if (!paymentStatus) return false;
    // 7. Paid amount must be provided and valid
    if (paidAmount === "" || paidAmount === null || paidAmount === undefined)
      return false;
    if (parseFloat(paidAmount) < 0) return false;
    if (parseFloat(paidAmount) > calculateTotal()) return false;
    return true;
  }, [
    purchaseType,
    selectedSite,
    materials,
    paymentMethod,
    paymentStatus,
    paidAmount,
  ]);

  // const handleCreatePurchase = async () => {
  //   setDeliveryDateError(null);
  //   setSelectedMaterialError(null);
  //   setQuantityError(null);
  //   setRateError(null);
  //   setPaidAmountError(null);
  //   setProjectError(null);

  //   if (!deliveryDate) {
  //     addAlert("Please select a delivery date", "error");
  //     setDeliveryDateError("Delivery date is required");
  //     return;
  //   }
  //   if (materials.length === 0) {
  //     addAlert("Please add at least one material", "error");
  //     setSelectedMaterialError("At least one material is required");
  //     return;
  //   }
  //   const invalidMaterials = materials.filter(
  //     (mat) => !mat.materialId || !mat.name || mat.name === "Select Material",
  //   );
  //   if (invalidMaterials.length > 0) {
  //     addAlert("Please select a material for all added items", "error");
  //     setSelectedMaterialError("Please select a material");
  //     return;
  //   }
  //   const incompleteMaterials = materials.filter(
  //     (mat) =>
  //       !mat.quantity ||
  //       !mat.rate ||
  //       parseFloat(mat.quantity) <= 0 ||
  //       parseFloat(mat.rate) <= 0,
  //   );
  //   if (incompleteMaterials.length > 0) {
  //     addAlert(
  //       "Please enter valid quantity and rate for all materials",
  //       "error",
  //     );
  //     setSelectedMaterialError("Invalid rate or quantity");
  //     return;
  //   }
  //   if (paidAmount === "" || paidAmount === null || paidAmount === undefined) {
  //     addAlert("Please enter the paid amount", "error");
  //     setPaidAmountError("Paid amount is required");
  //     return;
  //   }
  //   const totalAmount = calculateTotal();
  //   const paid = parseFloat(paidAmount);
  //   if (paid > totalAmount) {
  //     addAlert("Paid amount cannot exceed the total amount", "error");
  //     setPaidAmountError("Paid amount exceeds total");
  //     return;
  //   }
  //   if (paid < 0) {
  //     addAlert("Paid amount cannot be negative", "error");
  //     setPaidAmountError("Paid amount is invalid");
  //     return;
  //   }
  //   if (purchaseType === "Project" && !selectedSite) {
  //     addAlert("Please select a site for project purchase", "error");
  //     setProjectError("Site selection is required for project purchase");
  //     return;
  //   }
  //   if (!paymentMethod) {
  //     addAlert("Please select a Payment Method", "error");
  //     return;
  //   }
  //   if (!paymentStatus) {
  //     addAlert("Please select a Payment Status", "error");
  //     return;
  //   }

  //   try {
  //     const payload = {
  //       organization: organisationId,
  //       site: purchaseType === "Project" ? selectedSite?.id : null,
  //       vendor: vendor.id,
  //       order_date: deliveryDate,
  //       transaction_type: deliveryStatus,
  //       status: deliveryStatus === "Delivered" ? "completed" : "approved",
  //       payment_method: paymentMethod,
  //       payment_status: paymentStatus,
  //       paid_amount: parseFloat(paidAmount) || 0,
  //       comment: remarks,
  //       status_reason:
  //         purchaseType === "Organisation"
  //           ? "sent_to_warehouse"
  //           : "send_to_project",
  //       lines: materials.map((mat) => ({
  //         material: mat.materialId,
  //         quantity: parseFloat(mat.quantity) || 0,
  //         unit_price: parseFloat(mat.rate) || 0,
  //         tax_amount: mat.gstAmount || 0,
  //         total_price: mat.total || 0,
  //         status: deliveryStatus === "Delivered" ? "completed" : "approved",
  //       })),
  //     };
  //     const accessToken = localStorage.getItem("access");
  //     const res = await fetch(`${BASE_URL}/inventory/purchase-orders/create/`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${accessToken}`,
  //       },
  //       body: JSON.stringify(payload),
  //     });
  //     if (!res.ok) {
  //       addAlert("Failed to create purchase", "error");
  //       throw new Error("Failed to create purchase");
  //     }
  //     await res.json();
  //     onClose();
  //     addAlert("Purchase created successfully", "success");
  //   } catch (error) {
  //     console.error("Error creating purchase:", error);
  //     addAlert("Error creating purchase", "error");
  //   }
  // };
  const handleCreatePurchase = async () => {
    setDeliveryDateError(null);
    setSelectedMaterialError(null);
    setQuantityError(null);
    setRateError(null);
    setPaidAmountError(null);
    setProjectError(null);
    setPurchaseTypeError(null);

    // Validation 0: Check if purchase type is selected
    if (purchaseType === "null" || !purchaseType) {
      addAlert("Please select a purchase type", "error");
      setPurchaseTypeError("Purchase type is required");
      return;
    }

    // Validation 1: Check if delivery date is selected
    if (!deliveryDate) {
      addAlert("Please select a delivery date", "error");
      setDeliveryDateError("Delivery date is required");
      return;
    }
    if (materials.length === 0) {
      addAlert("Please add at least one material", "error");
      setSelectedMaterialError("At least one material is required");
      return;
    }
    const invalidMaterials = materials.filter(
      (mat) => !mat.materialId || !mat.name || mat.name === "Select Material",
    );
    if (invalidMaterials.length > 0) {
      addAlert("Please select a material for all added items", "error");
      setSelectedMaterialError("Please select a material");
      return;
    }
    const incompleteMaterials = materials.filter(
      (mat) =>
        !mat.quantity ||
        !mat.rate ||
        parseFloat(mat.quantity) <= 0 ||
        parseFloat(mat.rate) <= 0,
    );
    if (incompleteMaterials.length > 0) {
      addAlert(
        "Please enter valid quantity and rate for all materials",
        "error",
      );
      setSelectedMaterialError("Invalid rate or quantity");
      return;
    }
    if (paidAmount === "" || paidAmount === null || paidAmount === undefined) {
      addAlert("Please enter the paid amount", "error");
      setPaidAmountError("Paid amount is required");
      return;
    }
    const totalAmount = calculateTotal();
    const paid = parseFloat(paidAmount);
    if (paid > totalAmount) {
      addAlert("Paid amount cannot exceed the total amount", "error");
      setPaidAmountError("Paid amount exceeds total");
      return;
    }
    if (paid < 0) {
      addAlert("Paid amount cannot be negative", "error");
      setPaidAmountError("Paid amount is invalid");
      return;
    }
    if (purchaseType === "Project" && !selectedSite) {
      addAlert("Please select a site for project purchase", "error");
      setProjectError("Site selection is required for project purchase");
      return;
    }
    if (!paymentMethod && paymentStatus !== "Full Due") {
      addAlert("Please select a Payment Method", "error");
      return;
    }
    if (!paymentStatus) {
      addAlert("Please select a Payment Status", "error");
      return;
    }

    try {
      const accessToken = localStorage.getItem("access");

      // ── Step 1: Create the PO with JSON ──────────────────────────────────────
      const payload = {
        organization: organisationId,
        site: purchaseType === "Project" ? selectedSite?.id : null,
        vendor: vendor.id,
        order_date: deliveryDate,
        transaction_type: deliveryStatus,
        status: deliveryStatus === "Delivered" ? "completed" : "approved",
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        paid_amount: parseFloat(paidAmount) || 0,
        comment: remarks,
        status_reason:
          purchaseType === "Organisation"
            ? "sent_to_warehouse"
            : "send_to_project",
        lines: materials.map((mat) => ({
          material: mat.materialId,
          quantity: parseFloat(mat.quantity) || 0,
          unit_price: parseFloat(mat.rate) || 0,
          tax_amount: mat.gstAmount || 0,
          total_price: mat.total || 0,
          status: deliveryStatus === "Delivered" ? "completed" : "approved",
        })),
      };

      const res = await fetch(`${BASE_URL}/inventory/purchase-orders/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Purchase created successfully",
        errorMessage: "Failed to create purchase",
      });

      if (!res.ok || !feedback.success) {
        addAlert(feedback.message, "error");
        return;
      }

      const newPoId = data?.id || data?.data?.id;

      // ── Step 2: If receipts were attached, upload them now ───────────────────
      if (receiptFiles.length > 0 && newPoId) {
        const formData = new FormData();
        formData.append("purchase_order", newPoId);
        receiptFiles.forEach((file) => {
          formData.append("receipt_files", file);
        });

        const uploadRes = await fetch(
          `${BASE_URL}/inventory/upload-purchase-receipts/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              // No Content-Type — browser sets multipart boundary automatically
            },
            body: formData,
          },
        );

        if (!uploadRes.ok) {
          // PO was created successfully; just warn about the receipt upload
          addAlert(
            "Purchase created but receipt upload failed. You can attach it via Payment History.",
            "warning",
          );
        }
      }

      onClose();
      addAlert(feedback.message, feedback.alertType);
    } catch (error) {
      console.error("Error creating purchase:", error);
      addAlert(error.message || "Error creating purchase", "error");
    }
  };

  return (
    <div className="w-full h-full min-h-0 text-black flex flex-col">
      {/* Header */}
        <div className="flex items-center justify-between py-2.5 border-b border-gray-200 px-1">
          <div className="relative w-full flex items-center justify-center py-2">
            <p className="text-[#232323] text-[20px]">Material Purchase</p>
            <button
              onClick={onClose}
              className="absolute right-4 p-1.5 hover:bg-gray-100 rounded-full transition"
              type="button"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            {/* ─── Clear button — top right, mirrors Purchase Order ─── */}
            <button
              onClick={handleClear}
              className="absolute right-14 text-blue-600 text-sm hover:underline"
            >
              Clear
            </button>
          </div>
        </div>


      <div className="overflow-y-auto overflow-x-hidden flex-1 min-h-0">
        <div className="px-4 pt-2 pb-3">
          <div className="p-4">
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10">
                  <img
                    src={vendor_profile_icon}
                    alt="Vendor Icon"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <div className="text-[#232323] text-[16px]">
                    {vendor?.name}
                    <span className="text-[12px] bg-gray-200 px-2 py-1 rounded-full ml-2 text-gray-600">
                      Vendor
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-4">
                  <label className="text-[#232323] text-[15px] whitespace-nowrap">
                    Purchase Type
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setPurchaseType("Project");
                        setPurchaseTypeError(null);
                      }}
                      className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                        purchaseType === "Project"
                          ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      Projects
                    </button>
                    <button
                      onClick={() => {
                        setPurchaseType("Organisation");
                        setSelectedSite(null);
                        setPurchaseTypeError(null);
                      }}
                      className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                        purchaseType === "Organisation"
                          ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      Warehouse
                    </button>
                  </div>
                </div>
                {purchaseTypeError && (
                  <p className="text-red-500 text-sm mt-1">
                    {purchaseTypeError}
                  </p>
                )}
              </div>

              {purchaseType === "Project" && (
                <div>
                  <label className="text-[#232323] text-[15px] mb-2 block">
                    Select Site
                  </label>
                  <div
                    className="w-full p-2 pr-3 cursor-pointer border rounded-lg border-gray-300 justify-between flex flex-row items-center bg-gray-100"
                    onClick={() => setShowSitesDropdown(!showSitesDropdown)}
                  >
                    {selectedSite ? selectedSite.site_name : "Select a site"}
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {showSitesDropdown && (
                    <div className="border border-gray-300 rounded-lg mt-1 max-h-40 overflow-y-auto bg-white z-10 relative">
                      {sites.length > 0 ? (
                        sites.map((site) => (
                          <div
                            key={site.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setSelectedSite(site);
                              setShowSitesDropdown(false);
                            }}
                          >
                            {site.site_name}
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-gray-500">
                          No sites available
                        </div>
                      )}
                    </div>
                  )}
                  {projectError && (
                    <p className="text-red-500 text-sm mt-1">{projectError}</p>
                  )}
                </div>
              )}

              {withPO && (
                <div>
                  <label className="text-[#232323] text-[15px] mb-2 block">
                    Select Purchase Order
                  </label>
                  <select className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100">
                    <option>Select PO</option>
                  </select>
                </div>
              )}

              {/* Delivery Date */}
              <div>
                <label className="text-[#232323] text-[15px] mb-2 block">
                  Select Delivery Date
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg pr-10 bg-gray-100"
                  />
                </div>
                {deliveryDateError && (
                  <p className="text-red-500 text-sm mt-1">
                    {deliveryDateError}
                  </p>
                )}
              </div>

              {/* Delivery Status */}
              <div className="flex items-center space-x-3">
                <label className="text-[#232323] text-[15px] mb-2 block">
                  Delivery Status
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setDeliveryStatus("Upcoming")}
                    className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                      deliveryStatus === "Upcoming"
                        ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Upcoming
                  </button>
                  <button
                    onClick={() => setDeliveryStatus("Delivered")}
                    className={`px-2 py-1 rounded-full text-sm w-fit h-fit transition-colors ${
                      deliveryStatus === "Delivered"
                        ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Delivered
                  </button>
                </div>
              </div>

              {/* Materials Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-base text-[18px] text-[#232323]">
                      Materials
                      <span className="text-red-500 ml-1">*</span>
                    </h3>
                    <span className="text-[12px] bg-gray-200 px-2 py-1 rounded-full ml-2 text-gray-600">
                      {materials.length}
                    </span>
                  </div>
                  <button
                    onClick={addMaterial}
                    className="text-[#3B0069] hover:text-[#3B0069]"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {materials.map((material, index) => (
                  <div key={material.id} className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-full flex flex-row cursor-pointer items-center justify-between px-3 py-2 rounded-lg bg-[#F3F4F6] border border-gray-300"
                        onClick={() => handleOpenMaterialSelector(material.id)}
                      >
                        <div className="w-full bg-transparent border-0 text-[#232323] font-normal text-sm focus:outline-none">
                          {material?.name || "Select Material"}
                        </div>
                        <span className="flex flex-col items-center justify-center">
                          <MdExpandMore color="#232323" size={22} />
                        </span>
                      </div>
                      <button
                        onClick={() => removeMaterial(material.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {showMaterialSelector && activeMaterialId === material.id && (
                      <MaterialSelectionModal
                        isOpen={showMaterialSelector}
                        onClose={() => {
                          setShowMaterialSelector(false);
                          setActiveMaterialId(null);
                        }}
                        materialsLibrary={library}
                        selectedMaterials={materials}
                        activeMaterialId={activeMaterialId}
                        onSelectMaterial={handleSelectMaterialFromModal}
                      />
                    )}

                    <div className="flex items-center gap-2">
                      {/* Quantity — numeric only */}
                      <div className="relative w-full">
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="Enter Quantity"
                          value={material.quantity}
                          onChange={(e) => {
                            const sanitized = e.target.value
                              .replace(/[^0-9.]/g, "")
                              .replace(/(\..*)\./g, "$1");
                            updateMaterial(material.id, "quantity", sanitized);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm pr-10 bg-gray-100"
                        />
                      </div>

                      <div
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100"
                        value={material.unit}
                      >
                        <option>{material.unit}</option>
                      </div>

                      <div className="flex-shrink-0 px-2">
                        <X className="h-5 w-5 text-[#232323] cursor-pointer" />
                      </div>

                      {/* Rate — numeric only */}
                      <div className="relative w-full">
                        <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#232323] w-4 h-4" />
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="Rate"
                          value={material.rate}
                          onChange={(e) => {
                            const sanitized = e.target.value
                              .replace(/[^0-9.]/g, "")
                              .replace(/(\..*)\./g, "$1");
                            updateMaterial(material.id, "rate", sanitized);
                          }}
                          className="pl-8 pr-4 py-2 border rounded-lg text-sm w-full text-right bg-gray-100"
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-2">
                      <div className="flex gap-2">
                        {gstSlabs.map((slab) => (
                          <button
                            key={slab}
                            onClick={() =>
                              updateMaterial(
                                material.id,
                                "gst",
                                material.gst === slab ? null : slab,
                              )
                            }
                            className={`px-2 py-1 text-[13px] rounded-full border transition-colors ${
                              material.gst === slab
                                ? "bg-[#3B0069] text-white border-[#3B0069]"
                                : "bg-[#ECE6F0] text-[#3B0069] border-[#ECE6F0]"
                            }`}
                          >
                            {slab}%
                          </button>
                        ))}
                      </div>
                    </div>

                    {material.gst && (
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <span>GST ({material.gst}%)</span>
                        <span>₹ {material.gstAmount.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <div className="text-[15px] text-[#232323] text-left">
                        Total Price
                      </div>
                      <div className="text-base text-[#232323] text-right">
                        ₹ {Number(material?.total).toFixed(2)}
                      </div>
                    </div>

                    {index < materials.length - 1 && (
                      <hr className="my-3 border-gray-300" />
                    )}
                  </div>
                ))}

                {selectedMaterialError && (
                  <p className="text-red-500 text-sm mt-1">
                    {selectedMaterialError}
                  </p>
                )}
                {quantityError && (
                  <p className="text-red-500 text-sm mt-1">{quantityError}</p>
                )}
                {rateError && (
                  <p className="text-red-500 text-sm mt-1">{rateError}</p>
                )}
              </div>

              {/* Grand Total */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-[18px] text-[#232323]">
                  <span>Grand Total</span>
                  <span>₹ {calculateTotal().toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Payment Status */}
              <div className="flex items-center space-x-4">
                <label className="text-[#232323] text-[15px] mb-2 block">
                  Payment Status
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setPaymentStatus("Partial Paid");
                      setPaidAmount("");
                    }}
                    className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                      paymentStatus === "Partial Paid"
                        ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Partial Paid
                  </button>
                  <button
                    onClick={() => {
                      setPaymentStatus("Full Paid");
                      setPaidAmount(calculateTotal().toString());
                    }}
                    className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                      paymentStatus === "Full Paid"
                        ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Full Paid
                  </button>
                  <button
                    onClick={() => {
                      setPaymentStatus("Full Due");
                      setPaidAmount("0");
                    }}
                    className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                      paymentStatus === "Full Due"
                        ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    Full Due
                  </button>
                </div>
              </div>

              {/* Payment Method */}
              {paymentStatus !== "Full Due" && (
                <div className="flex items-center space-x-4">
                  <label className="text-[#232323] text-[15px] mb-2 block">
                    Payment Method
                    {/* <span className="text-red-500">*</span> */}
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPaymentMethod("Cash")}
                      className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                        paymentMethod === "Cash"
                          ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      Cash
                    </button>
                    <button
                      onClick={() => setPaymentMethod("Online")}
                      className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                        paymentMethod === "Online"
                          ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      Online
                    </button>
                    <button
                      onClick={() => setPaymentMethod("Cheque")}
                      className={`px-2 py-1 rounded-full w-fit h-fit text-sm transition-colors ${
                        paymentMethod === "Cheque"
                          ? "bg-[#ECE6F0] text-[#3B0069] border-1 border-[#3B0069]"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      Cheque
                    </button>
                  </div>
                </div>
              )}

              {/* Paid Amount */}
              <div className="flex flex-col gap-1 mb-4">
                <div className="flex items-center gap-4">
                  <label className="text-[#232323] text-[15px] whitespace-nowrap">
                    Paid Amount
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative w-full">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Amount"
                      disabled={paymentStatus !== "Partial Paid"}
                      style={{
                        cursor:
                          paymentStatus !== "Partial Paid"
                            ? "not-allowed"
                            : "auto",
                      }}
                      className="pl-8 pr-4 py-2 border rounded-lg text-sm w-full bg-gray-100"
                      value={paidAmount}
                      onChange={(e) => {
                        const sanitized = e.target.value
                          .replace(/[^0-9.]/g, "")
                          .replace(/(\..*)\./g, "$1");
                        setPaidAmount(sanitized);
                        const paid = parseFloat(sanitized) || 0;
                        if (paid > calculateTotal()) {
                          setPaidAmountError(
                            "Paid amount cannot exceed total ₹" +
                              calculateTotal().toLocaleString(),
                          );
                        } else {
                          setPaidAmountError(null);
                        }
                      }}
                    />
                  </div>
                </div>
                {paidAmountError && (
                  <p className="text-red-500 text-sm ml-[100px]">
                    {paidAmountError}
                  </p>
                )}
              </div>
              {/* Remaining Payment */}
              <div className="flex items-center justify-between w-full mb-2">
                <span className="text-[#232323] text-[16px]">
                  Remaining Payment
                </span>
                <span className="text-[16px] text-[#232323]">
                  ₹{" "}
                  {(
                    calculateTotal() - (parseFloat(paidAmount) || 0)
                  ).toLocaleString()}
                </span>
              </div>

              {/* Remarks */}
              <div>
                <label className="text-[#232323] text-[15px] mb-2 block">
                  Remarks/ Comments (Optional)
                </label>
                <textarea
                  type="text"
                  placeholder="Type"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg resize-y min-h-[60px] bg-gray-100"
                />
              </div>

              {/* Payment Slip */}
              <div>
                <label className="text-[#232323] text-[15px] mb-2 block">
                  Payment Slip (Optional)
                </label>
                <label className="w-full h-[36px] flex items-center justify-center px-4 py-8 bg-[#ECE6F0] rounded-lg text-sm text-[#3B0069] cursor-pointer hover:bg-gray-200">
                  <span className="flex items-center gap-2">
                    <Upload size={16} /> Upload Photos
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={handleReceiptUpload}
                    className="hidden"
                  />
                </label>
                {receiptFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {receiptFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <div className="text-xs text-gray-600 truncate">
                            {file.name}
                          </div>
                          <div className="text-xs text-gray-400">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </div>
                        </div>
                        <button
                          onClick={() => removeReceipt(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Save & Purchase button — brightens only when ALL required fields filled ─── */}
      <div className="mt-0 mb-0 pb-2 flex justify-center">
        <button
          onClick={handleCreatePurchase}
          disabled={!isFormValid}
          className={`bg-[#3B0069] text-white px-10 py-2 rounded-[12px] font-medium hover:bg-purple-800 flex items-center gap-2 transition-opacity duration-200 ${
            !isFormValid ? "opacity-50 cursor-not-allowed" : "opacity-100"
          }`}
        >
          Save &amp; Purchase
          <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        </button>
      </div>

    </div>
  );
};

export default VendorPurchase;
